// Structured interaction TEMPLATES, keyed by the interaction topic (the string
// must match the CRM_INTERACTION_TOPICS option exactly). When the team picks a
// templated topic in the interaction form, these fields render in place of the
// free-text summary; the answers are stored as `structured_data` (JSON) and an
// auto-rendered readable `summary`. Add/edit a template here — nothing else
// changes. Field types: 'subjects' (multi-select of subject names), 'select'
// (dropdown), 'text' (multiline).
export const INTERACTION_TEMPLATES = {
  'New Program Enquiry': {
    fields: [
      { key: 'programs', label: 'Program(s) interested in', type: 'subjects' },
      {
        key: 'heard_about',
        label: 'How did they hear about Openhouse',
        type: 'select',
        options: [
          'Friends & Family',
          'Instagram',
          'Google Search',
          'Walk-in / Signage',
          'Other Social Media',
          'Existing parent referral',
          'Other'
        ]
      },
      {
        key: 'distance',
        label: 'Distance from centre',
        type: 'select',
        options: ['< 3 km', '3–5 km', '> 5 km']
      },
      {
        key: 'intent',
        label: 'Intent',
        type: 'select',
        options: ['High', 'Medium', 'Low']
      },
      { key: 'intent_reason', label: 'Intent — why', type: 'text' },
      { key: 'pricing_concerns', label: 'Pricing concerns', type: 'text' },
      { key: 'teacher_concerns', label: 'Teacher concerns', type: 'text' },
      { key: 'curriculum_concerns', label: 'Curriculum concerns', type: 'text' },
      { key: 'logistical_concerns', label: 'Logistical concerns', type: 'text' },
      { key: 'next_steps', label: 'Next steps', type: 'text' },
      { key: 'anything_else', label: 'Anything else', type: 'text' }
    ]
  },
  Recharge: {
    fields: [
      {
        key: 'will_recharge',
        label: 'Will recharge',
        type: 'select',
        // "Already recharged" forces no follow-up date: the parent has paid,
        // so there is nothing to call back about (Oindrila Roy / Arindam
        // Mitra, Salt Lake — logged as Yes, forced to pick a date, sat in
        // Focus as "due today").
        options: ['Yes', 'No', 'Undecided', 'Already recharged']
      },
      {
        key: 'recharge_reason',
        label: 'If no, reason',
        type: 'text',
        showWhen: { field: 'will_recharge', equals: 'No' }
      },
      {
        key: 'finding_classes',
        label: "How they're finding the classes",
        type: 'text'
      },
      { key: 'concerns', label: 'Any concerns', type: 'text' }
    ]
  },
  'Discovery Enrolment': {
    fields: [
      {
        key: 'will_subscribe',
        label: 'Will subscribe',
        type: 'select',
        options: ['Yes', 'No', 'Undecided']
      },
      {
        key: 'subscribe_reason',
        label: 'If not, why',
        type: 'text',
        showWhen: { field: 'will_subscribe', equals: 'No' }
      },
      {
        key: 'finding_classes',
        label: 'How are they finding the classes',
        type: 'text'
      },
      { key: 'pricing_concerns', label: 'Pricing concerns', type: 'text' },
      { key: 'logistical_concerns', label: 'Logistical concerns', type: 'text' },
      { key: 'teacher_concerns', label: 'Teacher concerns', type: 'text' },
      { key: 'curriculum_concerns', label: 'Curriculum concerns', type: 'text' },
      { key: 'anything_else', label: 'Anything else', type: 'text' }
    ]
  }
}

// Topics renamed 2026-09-24 (user): New Program Enquiry → New Visitor and
// Discovery Enrolment → Discovery (see crm.constants.INTERACTION_TOPIC_RENAMES).
// The new names get the same template; the old keys stay so an interaction
// saved under the old name still opens with its fields.
INTERACTION_TEMPLATES['New Visitor'] = INTERACTION_TEMPLATES['New Program Enquiry']
INTERACTION_TEMPLATES.Discovery = INTERACTION_TEMPLATES['Discovery Enrolment']

// A field is only in play when its showWhen condition is met (e.g. "If no,
// reason" exists only when the answer is No). Hidden fields are never rendered,
// never summarised and never required.
export const isTemplateFieldVisible = (field, data = {}) =>
  !field.showWhen || data[field.showWhen.field] === field.showWhen.equals

// EVERY visible field of a template is mandatory. Returns the labels of the
// ones still blank, so the form can block the save and say what's missing.
export const getMissingTemplateFields = (template, data = {}) =>
  template.fields
    .filter((f) => isTemplateFieldVisible(f, data))
    .filter((f) => {
      const val = data[f.key]
      if (Array.isArray(val)) return val.length === 0
      return val === undefined || val === null || String(val).trim() === ''
    })
    .map((f) => f.label)

// A "Will recharge" answer of Yes or Undecided makes the follow-up DATE
// compulsory (user 2026-09-24): that parent is exactly the one the team has to
// call back before the month turns, so "No follow-up needed" — and a Yes with
// no date picked — are refused on save. Only a Connected call counts: the
// templates render (and structured_data is saved) for a Connected interaction
// alone, so a stale answer left in the form after switching the outcome to DNP
// must not block a save on a field the team can no longer see.
const FOLLOW_UP_REQUIRED_RECHARGE_ANSWERS = ['Yes', 'Undecided']

// Returns the answer ('Yes' / 'Undecided') that makes the date compulsory, or
// null when the follow-up stays optional. Keyed on the template FIELD rather
// than the topic name, so any template that grows a will_recharge follows.
export const followUpRequiredByTopics = (values) => {
  if (values?.connected_status !== 'Connected') return null
  for (const row of values?.interaction_topics || []) {
    const template = INTERACTION_TEMPLATES[row?.topic]
    if (!template?.fields.some((f) => f.key === 'will_recharge')) continue
    const answer = row?.structured_data?.will_recharge
    if (FOLLOW_UP_REQUIRED_RECHARGE_ANSWERS.includes(answer)) return answer
  }
  return null
}

export const followUpRequiredReason = (answer) =>
  answer === 'Undecided'
    ? 'the parent is undecided about recharging'
    : 'the parent said they will recharge'

// The save-time check every interaction form runs (Grow & Retain drawer,
// parent dashboard edit, Interaction History page). A message when the save
// must be blocked, else null. Only a real 'YYYY-MM-DD' passes — null ("No
// follow-up") and the widget's 'yes' / '' sentinels are all refused.
export const requiredFollowUpDateProblem = (values) => {
  const answer = followUpRequiredByTopics(values)
  if (!answer) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(values?.follow_up_date || '')) return null
  return `Add a follow-up date — ${followUpRequiredReason(answer)}`
}

// Broadcast by the Interactions page when a save is blocked, so every rendered
// template turns its blank required fields red instead of only toasting.
export const INTERACTION_TEMPLATE_VALIDATE = 'INTERACTION_TEMPLATE_VALIDATE'

// Compose the human-readable summary (HTML) from the filled template answers,
// so interaction history + any note-parsing keep working on `summary` as before.
export const renderTemplateSummary = (template, data = {}) => {
  const lines = []
  template.fields.forEach((f) => {
    // Skip fields hidden by an unmet showWhen condition (e.g. "If no, reason"
    // when the answer is Yes) so a stale value never leaks into the summary.
    if (!isTemplateFieldVisible(f, data)) return
    let val = data[f.key]
    if (Array.isArray(val)) val = val.join(', ')
    if (val === undefined || val === null || String(val).trim() === '') return
    lines.push(`<b>${f.label}:</b> ${val}`)
  })
  return lines.join('<br/>')
}
